import { useContext } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { TableCoreResourceContext } from 'components/table/TableCoreResourceContext';
import { ITableCoreResourceContext, UseCoreTableOptions } from 'components/table/types';
import { useCoreTableState } from 'components/table/useCoreTableState';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useIsPending } from 'customHooks/useIsPending';
import { ReportResource } from 'types/domain';

const useCoreTable = <N extends ReportResource>(
    resource: N,
    options: UseCoreTableOptions<N> = {},
) => {
    const { queryOptions = {}, tableStateOptions = {} } = options;
    const {
        queryKey: queryKeyConsumer,
        queryFn: queryFnConsumer,
        options: queryOptionsConsumer,
        delay = 500,
    } = queryOptions;
    const { accountId } = useCurrentAccount();
    const { queryKey: queryKeyContext, queryFn: queryFnContext } =
        useContext<ITableCoreResourceContext<N>>(TableCoreResourceContext);
    const tableState = useCoreTableState<N>(resource, tableStateOptions);
    const { params: tableParams } = tableState;
    const [isPending, params] = useIsPending(tableParams, delay);

    const queryKey = queryKeyContext ?? queryKeyConsumer;
    const queryFn = queryFnContext ?? queryFnConsumer;

    if (!queryKey) {
        throw new Error('Must provide a query key');
    }

    if (!queryFn) {
        throw new Error('Must provide a query function');
    }

    const query = useSuspenseQuery({
        queryKey: queryKey(accountId, params),
        queryFn: () => queryFn(accountId, params),
        ...queryOptionsConsumer,
    });

    return { isPending, query, ...tableState };
};

export { useCoreTable };
